import { h, setChildren } from "../core/dom.js";
import { loadLibrary, muscleName } from "../store.js";
import { icon } from "../lib/icons.js";
import { exerciseAlternatives, equipmentKey } from "../lib/exerciseAlternatives.js";
import { exerciseThumb } from "./common.js";
import { openExerciseDetail } from "./exerciseDetailModal.js";
import { openModal, spinner, errorState } from "./ui.js";

const titleCase = (s) => s ? s[0].toUpperCase() + s.slice(1) : "Equipment not listed";
const metricLabel = (ex) => ({ weight_reps: "Weight × reps", bodyweight_reps: "Reps", duration: "Time", distance_duration: "Distance + time" }[ex.metric_type || "weight_reps"]);

export function openExerciseReplacement({ exercise, existing, onReplace, onClose }) {
  let library = [], selected = null, scope = "session", busy = false, closed = false;
  let query = "", equipment = "", limit = 12, lastChoice = null;
  const body = h("div", { class: "replacement" }, spinner("Finding alternatives…"));
  const modal = openModal({ title: "Replace exercise", content: body, className: "replacement-modal", canClose: () => !busy,
    onClose: () => { closed = true; onClose?.(); } });
  const main = h("div", { class: "replacement-main" });
  const footer = h("div", { class: "replacement-footer" });
  const results = h("div", { class: "replacement-results" });
  const count = h("p", { class: "replacement-count", role: "status" });
  const list = h("div", { class: "replacement-list" });
  const search = h("input", { class: "input", type: "search", placeholder: "Search alternatives…", "aria-label": "Search alternatives",
    oninput: (e) => { query = e.target.value; limit = 12; renderResults(); } });
  const equipmentSelect = h("select", { class: "select", "aria-label": "Filter equipment",
    onchange: (e) => { equipment = e.target.value; limit = 12; updateEquipment(); } });
  const same = h("button", { class: "replacement-filter", "aria-pressed": "false", disabled: !exercise.equipment,
    onClick: () => { equipment = equipment === equipmentKey(exercise.equipment) ? "" : equipmentKey(exercise.equipment); limit = 12; updateEquipment(); }
  }, icon("dumbbell", { size: 16 }), "Same equipment");
  const filters = h("div", { class: "replacement-controls" },
    h("div", { class: "replacement-search" }, icon("search", { size: 18 }), search),
    h("div", { class: "replacement-filters" }, same, equipmentSelect));
  const primaryNames = (exercise.primary_muscle_ids || []).map(muscleName).filter(Boolean);
  const source = h("div", { class: "replacement-source" }, exerciseThumb(exercise.images, { size: 48 }),
    h("div", { class: "grow" }, h("span", { class: "replacement-eyebrow" }, "REPLACING"),
      h("strong", null, exercise.exercise_name),
      h("span", { class: "replacement-meta" }, [titleCase(exercise.equipment), ...primaryNames].join(" · "))));
  const browser = h("div", { class: "replacement-browser" }, source,
    h("p", { class: "replacement-intro" }, "Same muscles. A different option."), filters, results);
  const more = h("button", { class: "btn btn-ghost btn-block", onClick: () => { limit += 12; renderResults(); } }, "Show more alternatives");
  load();

  async function load() {
    try {
      library = await loadLibrary();
      if (closed) return;
      const equipments = [...new Set(library.map((ex) => equipmentKey(ex.equipment)).filter(Boolean))].sort();
      setChildren(equipmentSelect, h("option", { value: "" }, "All equipment"), ...equipments.map((value) => h("option", { value }, titleCase(value))));
      showBrowser();
    } catch {
      if (!closed) setChildren(body, errorState("Couldn't load alternatives. Reconnect and try again.", load));
    }
  }

  function updateEquipment() {
    same.setAttribute("aria-pressed", String(!!equipment && equipment === equipmentKey(exercise.equipment)));
    equipmentSelect.value = equipment;
    renderResults();
  }

  function showBrowser() {
    selected = null;
    setChildren(main, browser);
    setChildren(footer, h("p", { class: "replacement-hint" }, icon("info", { size: 16 }), "Choose an alternative, then decide where to use it."));
    setChildren(body, main, footer);
    renderResults();
    if (lastChoice) queueMicrotask(() => list.querySelector(`[data-choice="${lastChoice}"]`)?.focus());
  }

  function renderResults() {
    const source = library.find((ex) => ex.id === exercise.library_exercise_id) || exercise;
    const matches = exerciseAlternatives(source, library, { equipment, query, existing });
    count.textContent = `${matches.length} alternative${matches.length === 1 ? "" : "s"} · Closest muscle matches first`;
    setChildren(results, count, list);
    if (!matches.length) {
      setChildren(list, h("div", { class: "replacement-empty" }, icon("search", { size: 30 }),
        h("h4", null, "No matching alternatives"),
        h("p", null, query || equipment ? "Try another search or include more equipment." : "No other exercises share this exercise’s primary muscles in the current catalog."),
        (query || equipment) && h("button", { class: "btn btn-ghost", onClick: () => { query = ""; equipment = ""; search.value = ""; updateEquipment(); } }, "Clear filters")));
      return;
    }
    setChildren(list, ...matches.slice(0, limit).map((match, index) => {
      const ex = match.exercise;
      return h("div", { class: ["replacement-option", index === 0 && "replacement-top"] },
        h("button", { class: "replacement-pick", "aria-label": `Choose ${ex.exercise_name}`, dataset: { choice: ex.id }, onClick: () => review(match) },
          exerciseThumb(ex.images, { size: 52 }),
          h("span", { class: "replacement-option-copy" },
            index === 0 && h("span", { class: "replacement-eyebrow" }, icon("target", { size: 12 }), "CLOSEST MATCH"),
            h("strong", null, ex.exercise_name),
            h("span", { class: "replacement-meta" }, [titleCase(ex.equipment), ex.level && titleCase(ex.level)].filter(Boolean).join(" · ")),
            h("span", { class: "replacement-match" }, match.exact ? "Same primary muscles" : `Targets ${match.shared.map(muscleName).filter(Boolean).join(" & ")}`)),
          icon("forward", { size: 18 })),
        h("button", { class: "replacement-details", "aria-label": `View details for ${ex.exercise_name}`, onClick: () => openExerciseDetail(ex.id) }, icon("info", { size: 15 }), "Details"));
    }));
    if (matches.length > limit) results.append(more);
  }

  function review(match) {
    selected = match.exercise;
    lastChoice = selected.id;
    scope = "session";
    const completed = exercise.sets.filter((set) => set.is_completed).length;
    const remaining = exercise.sets.filter((set) => !set.is_completed).length || 1;
    const canSave = !!exercise.template_library_exercise_id;
    const heading = h("h4", { class: "replacement-review-title", tabindex: "-1" }, "Make it your next move");
    const scopeChoices = h("fieldset", { class: "replacement-scopes" }, h("legend", null, "Where should this change apply?"));
    for (const [value, label, description, glyph] of [
      ["session", "Just this session", "Your saved workout stays the same.", "timer"],
      ["workout", "This session + saved workout", canSave ? `Also replaces it in “${exercise.template_name}” for next time.` : "This exercise isn’t linked to a saved workout.", "calendar"],
    ]) {
      scopeChoices.append(h("label", { class: ["replacement-scope", value === "workout" && !canSave && "unavailable"] },
        h("input", { type: "radio", name: "replacement-scope", value, checked: value === scope, disabled: value === "workout" && !canSave,
          onchange: () => { scope = value; updateSaveLabel(); } }),
        icon(glyph, { size: 20 }),
        h("span", null, h("strong", null, label), h("small", null, description))));
    }
    const error = h("p", { class: "replacement-error", role: "alert" });
    const save = h("button", { class: "btn btn-primary btn-block replacement-save", onClick: async () => {
      if (busy) return;
      busy = true;
      // Prevent dismissing a mutation while the server is deciding its outcome.
      modal.panel.inert = true;
      save.textContent = "Replacing…";
      error.textContent = "";
      try {
        await onReplace(selected, scope);
        busy = false;
        modal.close();
      } catch (e) {
        error.textContent = e?.status === 0 ? "Connection lost. Reconnect and reopen this picker to check whether the change saved." : e.message || "Couldn't replace the exercise. Try again.";
      } finally {
        busy = false;
        modal.panel.inert = false;
        updateSaveLabel();
      }
    } });
    function updateSaveLabel() { save.textContent = scope === "workout" ? "Replace & update workout" : "Replace for this session"; }
    updateSaveLabel();
    setChildren(main, h("div", { class: "replacement-review" },
      h("button", { class: "back-link", onClick: showBrowser }, icon("back", { size: 16 }), "Alternatives"), heading,
      h("div", { class: "replacement-summary" },
        h("p", { class: "replacement-meta" }, `Instead of ${exercise.exercise_name}`),
        h("div", { class: "replacement-chosen" }, exerciseThumb(selected.images, { size: 64 }),
          h("div", null, h("strong", null, selected.exercise_name), h("p", { class: "replacement-meta" }, titleCase(selected.equipment)),
            h("p", { class: "replacement-match" }, match.exact ? "Same primary muscles" : `Targets ${match.shared.map(muscleName).filter(Boolean).join(" & ")}`))),
        h("button", { class: "replacement-details", onClick: () => openExerciseDetail(selected.id) }, icon("info", { size: 15 }), "Technique & details")),
      h("div", { class: "replacement-transfer" }, icon("check", { size: 18 }),
        h("p", null, `${remaining} ${completed ? "remaining " : ""}set${remaining === 1 ? "" : "s"} will move across. `,
          completed ? `${completed} logged set${completed === 1 ? " stays" : "s stay"} with ${exercise.exercise_name}. ` : "",
          selected.metric_type === "weight_reps" ? "Choose a fresh weight for this exercise." : `Log it using ${metricLabel(selected).toLowerCase()}.`)),
      scopeChoices));
    setChildren(footer, error, save);
    main.scrollTop = 0;
    queueMicrotask(() => heading.focus({ preventScroll: true }));
  }
  return modal;
}
